import { Link } from 'react-router-dom';
import { 
  Truck, Thermometer, Package, Zap, Clock, Users,
  CheckCircle, ArrowRight, Phone
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      id: 'dry-van',
      icon: Truck,
      title: 'Dry Van Freight',
      description: 'Our dry van service provides secure, enclosed transportation for general freight. With 53-foot trailers, we can handle a wide variety of palletized goods, boxed merchandise, and non-perishable products.',
      details: 'Dry van trailers offer complete protection from weather, road debris, and theft. Our trailers are equipped with E-track systems for secure cargo management and are regularly inspected for cleanliness and structural integrity.',
      bestFor: [
        'Consumer goods and retail merchandise',
        'Electronics and appliances',
        'Boxed and palletized freight',
        'Non-perishable food products',
        'Manufacturing materials',
      ],
      image: '/4.jpeg',
    },
    {
      id: 'reefer',
      icon: Thermometer,
      title: 'Reefer Freight',
      description: 'Temperature-controlled transportation for perishable goods. Our reefer units maintain precise temperatures from -20°F to 70°F, ensuring your products arrive in perfect condition.',
      details: 'Our refrigerated trailers feature state-of-the-art temperature monitoring systems with continuous logging. Drivers are trained in cold chain management and perform regular temperature checks throughout transit.',
      bestFor: [
        'Fresh produce and fruits',
        'Frozen foods and meats',
        'Dairy products',
        'Pharmaceuticals requiring temperature control',
        'Floral and plant shipments',
      ],
      image: '/5.jpeg',
    },
    {
      id: 'flatbed',
      icon: Package,
      title: 'Flatbed Transport',
      description: 'Specialized flatbed services for oversized, heavy, or irregularly shaped cargo. Our flatbed trailers accommodate loads that won\'t fit in enclosed trailers.',
      details: 'Our flatbed fleet includes standard flatbeds, step decks, and double-drop trailers. All equipment is maintained to the highest standards, and our drivers are experts in tarping, strapping, and securing diverse cargo types.',
      bestFor: [
        'Construction materials and steel',
        'Heavy machinery and equipment',
        'Lumber and building supplies',
        'Vehicles and large equipment',
        'Pipe, tubing, and structural materials',
      ],
      image: '/6.jpeg',
    },
    {
      id: 'power-only',
      icon: Zap,
      title: 'Power-Only',
      description: 'Have your own trailer? Our power-only service provides the tractor and driver to move your trailers between locations efficiently.',
      details: 'Power-only service is ideal for shippers with their own trailer fleet who need reliable capacity. We provide experienced drivers who understand trailer handling, dock procedures, and efficient routing.',
      bestFor: [
        'Drop and hook operations',
        'Trailer repositioning',
        'Seasonal capacity needs',
        'Companies with private trailer fleets',
        'Distribution center transfers',
      ],
      image: '/7.jpeg',
    },
    {
      id: 'box-truck',
      icon: Truck,
      title: 'Box Truck',
      description: 'Smaller shipments require smaller solutions. Our box truck service is perfect for LTL loads, local deliveries, and shipments that don\'t need a full trailer.',
      details: 'Box trucks offer flexibility for smaller loads with the added benefit of easier access to urban areas and facilities with limited dock space. Liftgate service is available for ground-level loading and unloading.',
      bestFor: [
        'LTL (Less Than Truckload) shipments',
        'Local and regional deliveries',
        'Residential deliveries',
        'Trade show and event freight',
        'Small business logistics',
      ],
      image: '/8.jpeg',
    },
    {
      id: 'expedited',
      icon: Clock,
      title: 'Expedited Freight',
      description: 'When time is critical, our expedited service delivers. Dedicated trucks, direct routes, and priority handling ensure your urgent shipments arrive on time.',
      details: 'Expedited freight moves on a dedicated basis with no stops or transfers. Our dispatch team provides real-time tracking and proactive communication throughout transit. Available 24/7 for emergency shipments.',
      bestFor: [
        'Time-critical manufacturing parts',
        'Emergency medical supplies',
        'Just-in-time inventory replenishment',
        'Event and production deadlines',
        'High-value rush shipments',
      ],
      image: '/9.jpeg',
    },
    {
      id: 'team-driving',
      icon: Users,
      title: 'Team Driving',
      description: 'For long-haul shipments that need to keep moving, our team drivers take turns at the wheel, minimizing stops and maximizing transit speed.',
      details: 'Team driving allows for nearly continuous movement across the country. With two qualified drivers sharing the cab, your freight keeps moving while one driver rests. Ideal for cross-country expedited shipments.',
      bestFor: [
        'Coast-to-coast expedited loads',
        'Time-sensitive cross-country shipments',
        'High-value cargo requiring minimal stops',
        'Perishable goods needing fast transit',
        'Critical manufacturing components',
      ],
      image: '/10.jpeg',
    },
  ];

  return (
    <div className="bg-white" data-testid="services-page">
      {/* Hero Header */}
      <section className="bg-[#0B2A5B] py-20" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#FFD700] text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
            Our Services
          </span>
          <h1 className="text-4xl md:text-5xl font-black text-white tracking-tight font-['Montserrat'] mb-4">
            Nationwide Freight Transportation Services
          </h1>
          <p className="text-white/70 max-w-2xl mx-auto">
            From dry van to expedited team driving, we have the equipment and expertise to handle your freight with care.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding" data-testid="services-list">
        <div className="max-w-5xl mx-auto space-y-20">
          {services.map((service, index) => (
            <div
              key={service.id}
              id={service.id}
              className={`grid lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
              data-testid={`service-block-${service.id}`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="flex items-center space-x-4 mb-6">
                  <div className="bg-[#0B2A5B] w-14 h-14 flex items-center justify-center rounded-sm">
                    <service.icon className="h-7 w-7 text-[#FFD700]" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#0B2A5B] font-['Montserrat']">
                    {service.title}
                  </h2>
                </div>
                
                <p className="text-[#6B7280] mb-4 leading-relaxed">
                  {service.description}
                </p>
                <p className="text-[#6B7280] mb-6 leading-relaxed">
                  {service.details}
                </p>

                <div className="bg-[#F8FAFC] p-6 rounded-sm border-l-4 border-[#C62828]">
                  <h4 className="text-sm font-bold text-[#0B2A5B] uppercase tracking-wider mb-4 font-['Montserrat']">
                    Best For:
                  </h4>
                  <ul className="space-y-2">
                    {service.bestFor.map((item, i) => (
                      <li key={i} className="flex items-start space-x-3">
                        <CheckCircle className="h-4 w-4 text-[#C62828] mt-1 flex-shrink-0" />
                        <span className="text-[#6B7280] text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-[350px] object-cover rounded-sm shadow-lg"
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-banner py-16" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 font-['Montserrat']">
            Ready to Ship?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Contact our dispatch team for a quote tailored to your specific freight needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="bg-white text-[#C62828] px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-slate-100 transition-all shadow-lg inline-flex items-center justify-center space-x-2"
              data-testid="services-cta-quote"
            >
              <span>Request a Quote</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:773-344-7748"
              className="border-2 border-white text-white px-8 py-4 font-bold uppercase tracking-wide rounded-sm hover:bg-white hover:text-[#C62828] transition-all inline-flex items-center justify-center space-x-2"
              data-testid="services-cta-call"
            >
              <Phone className="h-4 w-4" />
              <span>773-344-7748</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
